import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, SanitizedHtml} from 'yougile-util'
import {Hub, newsCompanyId, TaskHubData} from 'yougile-model'
import {EventBus} from '@client/event-bus'
import {onBeforeBrowserUnloadEvent} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showConfirm} from '@components/common/base/notifications/show-confirm'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {rightPanelStore} from '../right-panel/right-panel-store'
import {wrapLinks} from './wrap-links'

export interface State {
  edit: boolean
  fullscreen: boolean
  historyMode: boolean
  selectedHistoryAt: number

  content: string // orig task desc, need for cancel
  contentChanged: string // desc that changed by editor
  historyContent?: string // desc for a specific history item
  historyList: HistoryItem[]

  // need to not load historyContent for current desc
  alreadyLoadedHistoryContent?: boolean
}

type HistoryItem = {
  date: number
  userId: string
}

interface HistoryListResp {
  result: 'ok' | 'error'
  descriptionDataJson: string
  history: HistoryItem[]
}

interface HistoryDataResp {
  description: string
  date: number
  taskId: string
}

const initState: State = {
  edit: false,

  content: '',
  contentChanged: '',
  historyList: [],
  fullscreen: false,
  historyMode: false,
  selectedHistoryAt: 0
}

export class TaskDescriptionStore {
  state: State = initState
  taskHub: Hub

  constructor(taskHub: Hub) {
    this.taskHub = taskHub
    makeAutoObservable(this, {state: observable.deep}, {autoBind: true})

    EventBus.on(onBeforeBrowserUnloadEvent, this.checkUnsavedChangesOnUnload)
  }

  dispose(): void {
    EventBus.off(onBeforeBrowserUnloadEvent, this.checkUnsavedChangesOnUnload)
  }

  private checkUnsavedChangesOnUnload(): boolean {
    const required = this.isSaveRequired()
    if (required) {
      this.goBackToDescriptionTab()
    }
    return required
  }

  private isSaveRequired(): boolean {
    return this.state.edit && this.state.content !== this.state.contentChanged
  }

  private goBackToDescriptionTab(): void {
    const isPanelOpen = rightPanelStore.state.chatPanel.opened
    const isDescriptionTabActive = rightPanelStore.currentTab === 'description'
    if (!isPanelOpen) {
      rightPanelStore.chatPanelToggle()
    }
    if (!isDescriptionTabActive) {
      rightPanelStore.setTab('description', false)
    }
  }

  setEdit(edit: boolean): void {
    if (edit === this.state.edit) {
      return
    }

    if (edit) {
      if (this.state.fullscreen) {
        void Tracking.send(TrackingEvent.taskDescriptionFullScreenEdit)
      } else {
        void Tracking.send(TrackingEvent.taskDescriptionEdit)
      }
    }
    this.state.edit = edit
  }

  save(forceDescription?: string): void {
    this.setEdit(false)

    const desc = forceDescription ?? this.state.contentChanged ?? ''
    const preparedDesc = desc.replaceAll('&nbsp;', ' ')

    if (!this.taskHub?.id || this.state.content === preparedDesc) {
      return
    }

    const processedContent = wrapLinks(preparedDesc)

    if (this.state.content === processedContent) {
      this.state.content = processedContent
      this.state.contentChanged = processedContent
      this.state.historyContent = processedContent
      return
    }

    try {
      const taskHubData = this.taskHub.data as TaskHubData
      const date = +new Date()
      taskHubData.descriptionModifiedAt = date

      this.taskHub.description = processedContent

      Task.commitWithCheckPhantom(this.taskHub)

      this.state.content = processedContent
      this.state.contentChanged = processedContent
      this.state.historyContent = processedContent

      // send history
      void Backend.postJSON<{saveStatus: boolean}>('/data/description/save', {
        ...Backend.loadCred(),
        companyId: this.taskHub.companyId,
        userId: globals.currentUserId,
        taskId: this.taskHub.id,
        description: processedContent,
        date
      })
    } catch (e) {
      logger.error(e)
    }
  }

  saveHistory(): void {
    void Tracking.send(TrackingEvent.taskDescriptionHistoryVersionRecovery)
    void this.save(this.state.historyContent)
    this.setHistoryMode(false)
  }

  saveContent(): void {
    if (!this.state.edit) {
      return
    }

    if (this.state.fullscreen) {
      void Tracking.send(TrackingEvent.taskDescriptionFullScreenSave)
    } else {
      void Tracking.send(TrackingEvent.taskDescriptionSave)
    }

    void this.save()
  }

  async loadHistory(): Promise<void> {
    try {
      const resp = await Backend.postJSON<HistoryListResp>(
        '/data/description/get-list',
        {
          ...Backend.loadCred(),
          companyId: this.taskHub.companyId,
          taskId: this.taskHub.id
        }
      )

      if (resp.result !== 'ok') {
        logger.error(`Cannot get description for taskId=${this.taskHub.id}`)
        return
      }

      runInAction(() => {
        if (resp.descriptionDataJson) {
          const data: HistoryDataResp = JSON.parse(resp.descriptionDataJson)
          this.state.historyContent = data.description
        } else {
          this.state.historyContent = ''
        }

        if (resp.history?.length) {
          this.state.historyList = resp.history.reverse()
          this.state.selectedHistoryAt = this.state.historyList[0].date
        } else {
          this.state.historyList = []
          this.state.selectedHistoryAt = 0
        }
        this.state.alreadyLoadedHistoryContent = true
      })
    } catch (e) {
      logger.error('Cannot load description', e)
    }
  }

  async loadHistoryContent(): Promise<void> {
    if (this.state.alreadyLoadedHistoryContent) {
      return
    }

    const selectedAt = this.state.selectedHistoryAt
    if (!selectedAt) {
      return
    }

    try {
      const resp = await Backend.postJSON<HistoryListResp>(
        '/data/description/get-item',
        {
          ...Backend.loadCred(),
          companyId: this.taskHub.companyId,
          taskId: this.taskHub.id,
          date: selectedAt
        }
      )

      if (resp.result !== 'ok') {
        logger.error(`Cannot get description for taskId=${this.taskHub.id}`)
        return
      }

      runInAction(() => {
        if (resp.descriptionDataJson) {
          const data: HistoryDataResp = JSON.parse(resp.descriptionDataJson)
          this.state.historyContent = data.description
        } else {
          this.state.historyContent = '#empty'
        }
      })
    } catch (e) {
      logger.error('Cannot load description', e)
    }
  }

  startEdit(): void {
    void Tracking.send(TrackingEvent.taskEditDescription)
    const hub = this.taskHub
    const isNews = hub && hub.companyId === newsCompanyId
    const isNewsEditor = Sync.isNewsEditor

    if (isNews) {
      if (isNewsEditor) {
        this.setEdit(true)
      }
    } else {
      const isTaskTemplate = Task.isTaskTemplate(hub)
      if (
        hub &&
        !isTaskTemplate &&
        !PermissionsChecker.checkEditDescription(hub)
      ) {
        MessageBox.warn(Lang.msg.permissionsForbiddenEditDescription)
        return
      }
      this.setEdit(true)
    }
  }

  async cancelEdit(force = false): Promise<boolean> {
    if (this.state.content === this.state.contentChanged) {
      this.setEdit(false)
      return true
    }

    if (!force) {
      const focused = document.activeElement as HTMLElement
      const confirmed = await showConfirm(Lang.msg.editorCloseConfirm)
      if (!confirmed) {
        focused.focus()
        return false
      }
    }
    this.setEdit(false)
    this.state.contentChanged = this.state.content
    return true
  }

  onChange(data: string): void {
    if (this.state.edit) {
      this.state.contentChanged = data
    }
  }

  updateContent(html: SanitizedHtml): void {
    this.state.content = html
    this.state.contentChanged = html
    this.state.historyContent = html
  }

  onEscape(): boolean {
    if (this.state.edit) {
      void this.cancelEdit()
      return true
    } else if (this.state.fullscreen) {
      this.fullscreenToggle()
      return true
    }
    return false
  }

  onKeyDown(e: React.KeyboardEvent) {
    if (e.code === 'PageUp' || e.code === 'PageDown') {
      e.preventDefault()
    }

    if (e.code === 'Escape') {
      this.onEscape() && e.stopPropagation()
    }
  }

  fullscreenToggle(): void {
    const newValue = !this.state.fullscreen
    if (newValue) {
      void Tracking.send(TrackingEvent.taskDescriptionFullScreenOpen)
    }
    this.state.fullscreen = newValue
  }

  setHistoryMode(value: boolean): void {
    if (value) {
      void Tracking.send(TrackingEvent.taskDescriptionOpenHistory)
    }
    this.state.historyMode = value
  }

  selectHistoryItem(date: number): void {
    this.state.selectedHistoryAt = date
    this.state.alreadyLoadedHistoryContent = false
  }
}

const [TaskDescriptionContext, useTaskDescription] =
  createUseStore(TaskDescriptionStore)
export {TaskDescriptionContext, useTaskDescription}
